(() => {
  if (window.__neerMomentTerms) return;

  const ATTR = 'Moment Consent Accepted';
  // A tick counts for 2 hours (decisions.md, 2026-10-06): the same limit as moment_terms_max_age in cart-summary.liquid.
  const MAX_AGE_SECONDS = 2 * 60 * 60;
  // The time stamp() writes after the last " — "; any other text there is unreadable, so it is no consent.
  const STAMP_TIME = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(?:\.\d+)?Z$/;
  let latestRequest = 0;

  const stamp = (version) =>
    `Agreed \u2014 Moment Patient Release and Consent Agreement v${version} \u2014 ${new Date()
      .toISOString()
      .replace(/\.\d{3}Z$/, 'Z')}`;

  /** True when `value` is a stamp at most MAX_AGE_SECONDS old at `now` (whole seconds, as Liquid's date: '%s'). */
  const isConsentFresh = (value, now = Date.now()) => {
    const parts = typeof value === 'string' ? value.split(' \u2014 ') : [];
    while (parts.length && parts[parts.length - 1] === '') parts.pop(); // as Liquid's split (Ruby) drops them
    const time = parts.length ? parts[parts.length - 1] : '';
    const at = STAMP_TIME.test(time) ? Date.parse(time) : NaN;
    // A stamp ahead of this clock (a fast device) still counts.
    return Number.isFinite(at) && Math.floor(now / 1000) - Math.floor(at / 1000) <= MAX_AGE_SECONDS;
  };

  window.__neerMomentTerms = { isConsentFresh };

  // Shared lock rule from assets/dentist-assignment.js (installed when require_moment_range is on). Checkout follows the
  // box; express buttons need the box and data-terms-accepted (saved), so keep both in sync before refreshing.
  const lockHook = () => {
    const hook = window.__neerCheckoutLock;
    return hook && typeof hook.refresh === 'function' ? hook : null;
  };

  const setTermsAccepted = (accepted) => {
    document.querySelectorAll('[data-moment-terms]').forEach((wrap) => {
      if (accepted) wrap.dataset.termsAccepted = 'true';
      else delete wrap.dataset.termsAccepted;
    });
  };

  /** Without the hook, the same split per bag: Checkout and the hint follow `checkoutLocked`, express `expressLocked`. */
  const setLocked = (scope, { checkoutLocked, expressLocked }) => {
    const hook = lockHook();
    if (hook) {
      // Consent is synced across every bag instance, so refresh them all.
      hook.refresh(document);
      return;
    }
    scope.querySelectorAll('button[name="checkout"]').forEach((button) => {
      button.disabled = checkoutLocked || button.hasAttribute('data-base-disabled');
    });
    scope.querySelectorAll('.additional-checkout-buttons').forEach((box) => {
      // Express buttons also stay inert while the base lock (empty cart / no range result) applies.
      const ctas = box.closest('.cart__ctas');
      const boxLocked = expressLocked || Boolean(ctas && ctas.querySelector('button[name="checkout"][data-base-disabled]'));
      box.inert = boxLocked;
      if (boxLocked) box.setAttribute('aria-disabled', 'true');
      else box.removeAttribute('aria-disabled');
    });
    scope.querySelectorAll('[data-moment-terms-hint]').forEach((hint) => {
      hint.hidden = !checkoutLocked;
    });
  };

  const persist = (value) => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [ATTR]: value } }),
    }).then((response) => {
      if (!response.ok) throw new Error('cart update failed');
    });
  };

  // Saves that empty the stored consent (an untick, or a stale-stamp clear), from when they are queued until they
  // settle either way. A Checkout press waits for them (research D4); a tick's own save writes what the press posts.
  let emptying = 0;
  /** @type {Array<() => void>} */
  const whenEmptied = [];
  const countEmptying = (request) => {
    emptying += 1;
    const settle = () => {
      emptying -= 1;
      if (emptying === 0) whenEmptied.splice(0).forEach((resume) => resume());
    };
    request.then(settle, settle);
    return request;
  };

  // Consent writes go out one at a time, each once the one before has settled either way, so the cart ends on the latest
  // change (a tick's stamp lands after an earlier untick or stale clear). With none on its way, a write goes out at once.
  /** @type {Promise<void> | null} */
  let lastWrite = null;
  const save = (value) => {
    const request = lastWrite ? lastWrite.then(() => persist(value)) : persist(value);
    const settled = request.catch(() => {}); // a failed write doesn't hold up the next
    lastWrite = settled;
    settled.then(() => {
      if (lastWrite === settled) lastWrite = null;
    });
    return value === '' ? countEmptying(request) : request;
  };

  // Stale stamps already sent for clearing (each once per page).
  const cleared = new Set();

  const clearStale = (stamps) => {
    const due = stamps.filter((value) => !cleared.has(value));
    // A tick shown on the page replaces the stale stamp by itself; clearing now could land after it and undo it.
    const ticked = Array.from(document.querySelectorAll('input[data-moment-terms-attr]')).some((input) => input.value !== '');
    if (!due.length || ticked) return;
    due.forEach((value) => cleared.add(value));
    save('').catch(() => {
      due.forEach((value) => cleared.delete(value)); // tried again on the next sweep
    });
  };

  const dropTick = () => {
    document.querySelectorAll('[data-moment-terms-checkbox]').forEach((box) => {
      box.checked = false;
    });
    document.querySelectorAll('input[data-moment-terms-attr]').forEach((input) => {
      input.value = '';
    });
    setTermsAccepted(false);
    document
      .querySelectorAll('[data-moment-terms]')
      .forEach((wrap) => setLocked(wrap.closest('.cart__ctas') || document, { checkoutLocked: true, expressLocked: true }));
  };

  // Consent from an earlier visit or order never counts: Liquid marks it (expired, on a bag without a Moment, or on
  // an empty cart), or it is past MAX_AGE_SECONDS on this device's clock (a cached render, or a page left open).
  const sweep = () => {
    const marked = Array.from(document.querySelectorAll('[data-moment-consent-clear]'), (el) => el.getAttribute('data-moment-consent-clear'));
    const expired = Array.from(document.querySelectorAll('input[data-moment-terms-attr]'), (input) => input.value).filter(
      (value) => value && !isConsentFresh(value)
    );
    if (expired.length) dropTick();
    clearStale([...marked, ...expired].filter(Boolean));
  };

  document.addEventListener('change', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.matches('[data-moment-terms-checkbox]')) return;

    const wrap = box.closest('[data-moment-terms]');
    const scope = box.closest('.cart__ctas') || document;
    const value = box.checked ? stamp(wrap.dataset.termsVersion || '1.1') : '';

    document.querySelectorAll('input[data-moment-terms-attr]').forEach((input) => {
      input.value = value;
    });
    document.querySelectorAll('[data-moment-terms-checkbox]').forEach((other) => {
      other.checked = box.checked;
    });

    // Checkout follows the box at once: its press posts the stamp itself (the hidden input, form="cart-form").
    // Express (Shop Pay) checkout starts from the stored cart, so it waits for the newest save (data-terms-accepted).
    const request = ++latestRequest;
    setTermsAccepted(false);
    setLocked(scope, { checkoutLocked: !box.checked, expressLocked: true });

    save(value)
      .then(() => {
        if (request !== latestRequest || !box.checked) return;
        setTermsAccepted(true);
        setLocked(scope, { checkoutLocked: false, expressLocked: false });
      })
      // A failed save changes nothing (research D5): Checkout still posts the stamp, and express waits for a later save.
      .catch(() => {});
  });

  // An emptying save still unanswered after this is treated as failed, and the press goes ahead (research D4).
  const EMPTYING_SAVE_WAIT_MS = 4000;
  let holding = false;
  /** @type {HTMLButtonElement | null} */
  let resent = null;

  // An emptying save that lands after the Checkout form would wipe the stamp the form wrote (research F6), so a press
  // in a Moment bag waits for those saves. Capture phase, so no other click handler sees the held press.
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
      if (!(button instanceof HTMLButtonElement) || !button.closest('[data-checkout-lock][data-moment-in-cart="true"]')) return;
      if (resent === button || (!holding && emptying === 0)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (holding) return; // one press is already waiting
      holding = true;
      new Promise((resume) => {
        const timer = setTimeout(resume, EMPTYING_SAVE_WAIT_MS);
        whenEmptied.push(() => {
          clearTimeout(timer);
          resume();
        });
      }).then(() => {
        holding = false;
        if (button.disabled) return; // unticked (or locked) while waiting
        // Re-sent as a click, the pattern moment-range-bag.js uses: other click listeners see the press, and older
        // Safari has no requestSubmit.
        resent = button;
        try {
          button.click();
        } finally {
          resent = null;
        }
      });
    },
    true
  );

  // Re-rendered bags (a drawer morph) bring new markup; a back/forward restore or a return to the tab brings elapsed time.
  // This runs on every page, so other DOM changes (sliders, search) are skipped without a sweep.
  const WATCHED = '[data-moment-consent-clear], input[data-moment-terms-attr]';
  const touchesConsent = (records) =>
    records.some(
      (record) =>
        record.type === 'attributes' ||
        Array.from(record.addedNodes).some((node) => node instanceof Element && (node.matches(WATCHED) || node.querySelector(WATCHED) !== null))
    );
  new MutationObserver((records) => {
    if (touchesConsent(records)) sweep();
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-moment-consent-clear', 'value'],
  });
  window.addEventListener('pageshow', sweep);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sweep();
  });
  sweep();
})();
